import { fieldContainsValue, getQueries } from "./common";
import * as Sentry from "@sentry/react";
import { tokenManager } from "services/client/token-manager";
import { triggerLogout } from "services/client/logoutNotifier";
import client from "services/client";

export { getJwtPayload } from "services/client/jwt-utils";

const BASE_URL = process.env.REACT_APP_SERVER_URL;
const BASE_URL_V2 = process.env.REACT_APP_SERVER_URL_V2;
// const BASE_URL="https://qa-api.carrum.co.in/api/v1"

/** Try to refresh access token; returns true if new token set, false otherwise. */
async function tryRefreshAccessToken() {
  const refreshToken = tokenManager.getRefresh();
  if (!refreshToken) return false;
  try {
    const res = await client.post(
      `${BASE_URL_V2}/users/login/refresh`,
      {},
      { tokenType: "refresh" }
    );
    if (!res.ok) return false;
    // const data = await res.json();
    const access = res?.data?.accessToken ?? res?.data?.data?.accessToken;
    if (access) {
      // TOKEN_PERSIST_DUMMY: api2 refresh path — access (tokenManager)
      tokenManager.setAccess(access);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/** Clear tokens and redirect to login (e.g. when refresh token expired). */
function logoutAndRedirect() {
  tokenManager.clearAll();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("permission-refetch"));
  }
  triggerLogout({ sessionExpired: true });
}

const handleErrors = async (response) => {
  if (!response.ok) {
    let errorData = {};
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: "Invalid JSON response from server" };
    }
    // const error = new Error(errorData?.message || "An error occurred");
    const error = new Error(errorData?.error || errorData?.message || "An error occurred");
    error.status = response?.status;
    error.data = errorData;
    //TODO: // check need to handle --> HTTP unauthorized explicitly?
    // if (error.status === 401) {
    //   // store.dispatch(logout());
    // }
    if (
      error?.data?.error?.toLowerCase()?.includes("token") ||
      error?.data?.message?.toLowerCase()?.includes("token")
    ) {
      // alert("Session expired. Please try to login")
      return 0;
    }
    throw error;
  }
  return response.json();
};

// object api with http methods
const request = async (url, config) => {
  const defaultHeaders = {
    "Content-Type": "application/json",
    // "origin": "http://localhost:3000/",
    origin: `${process.env.REACT_APP_ORIGIN_URL}`,
    ...config.headers,
  };
  // const token = Cookies.get("authToken");
  const token = tokenManager.getAccess();
  if (token) {
    // defaultHeaders.Authorization = `Bearer ${token}`;
    defaultHeaders.Authorization = `carrum ${token}`;
  }

  try {
    const response = await fetch(`${BASE_URL}${url}`, {
      ...config,
      headers: defaultHeaders,
    });

    if (response.status === 401 && !config._retried) {
      const result = await response.json();
      if (
        result?.error?.toLowerCase()?.includes("token") ||
        result?.message?.toLowerCase()?.includes("token")
      ) {
        const refreshed = await tryRefreshAccessToken();
        if (refreshed) {
          return request(url, { ...config, _retried: true });
        }
        logoutAndRedirect();
      }
    }

    return handleErrors(response);
  } catch (err) {
    if (err?.data?.error === "Token Expired.") {
    }
    if (err?.data?.error === "token required") {
    }
    // if (!err?.status) {
    //   throw new Error("Network error or CORS issue");
    // }
    throw err;
  }
};

const api = {
  get: (url, config = {}) => request(url, { ...config, method: "GET" }),
  post: (url, data, config = {}) =>
    request(url, { ...config, method: "POST", body: JSON.stringify(data) }),
  put: (url, data, config = {}) =>
    request(url, { ...config, method: "PUT", body: JSON.stringify(data) }),
  delete: (url, config = {}) => request(url, { ...config, method: "DELETE" }),
  // postFile: (url, data, config={})=>request(url,{...config, method:'POST', body: data}),
  postFile: (url, data, config = {}) => {
    const { headers, ...restConfig } = config;
    const formDataHeaders = {
      ...headers,
    };
    delete formDataHeaders["Content-Type"]; // Do not set Content-Type header

    return request(url, {
      ...restConfig,
      method: "POST",
      body: data,
      headers: formDataHeaders,
    });
  },
};

export default api;

const withSentry = (fn) => {
  const is_sentry_on = process.env.REACT_APP_SENTRY === "true";

  // 🔹 If Sentry is OFF → no overhead
  if (!is_sentry_on) {
    return fn;
  }

  return async (params) => {
    const { method, url } = params;

    return Sentry.startSpan(
      {
        name: `${url}`,
        op: "http.client",
        attributes: {
          "span.type": "api_wrapper",
        },
      },
      async (span) => {
        if (typeof navigator !== "undefined" && navigator.connection) {
          span.setAttribute("network.type", navigator.connection.effectiveType);
        }

        try {
          const result = await fn(params);
          span.setAttribute("http.status_code", result.statusCode);
          span.setStatus({ code: 1, message: "ok" });
          return result;
        } catch (error) {
          span.setStatus({ code: 2, message: "internal_error" });
          if (error?.data?.statusCode) {
            span.setAttribute("http.status_code", error?.data?.statusCode);
          }
          span.setAttribute(
            "error.message",
            error?.data?.message || error?.message || "API failed"
          );
          span.setAttribute("error", true);

          // ✅ CAPTURE ERROR WITH FULL CONTEXT
          Sentry.withScope((scope) => {
            scope.setLevel("error");

            // Link error to this span / transaction
            scope.setContext("api", {
              method,
              url,
              statusCode: error?.data?.statusCode,
              params,
            });
            // Useful tags for filtering
            scope.setTag("api.method", method);
            scope.setTag("api.url", url);
            scope.setTag("api.layer", "wrapper");
            // Optional: attach response payload safely
            if (error?.data) {
              scope.setExtra("response", error.data);
            }
            scope.setExtra("original_error", error);
            Sentry.captureException(error);
          });
          throw error;
        }
      }
    );
  };
};

const connect = async ({
  method,
  url,
  json = false,
  formData = false,
  body = null,
  props = {},
  _retried,
}) => {
  // let authToken = Cookies.get("authToken");
  let authToken = tokenManager.getAccess();
  let refreshToken = tokenManager.getRefresh();
  const clientQueries = getQueries();
  let abortController = null;
  let abortId = null;

  if (!refreshToken && !fieldContainsValue(clientQueries?.apitoken)) {
    logoutAndRedirect();
  }
  if (fieldContainsValue(props?.token)) {
    authToken = props.token;
  }
  if (fieldContainsValue(clientQueries?.apitoken)) {
    authToken = clientQueries.apitoken;
  }
  let base_url = BASE_URL;
  let headers;
  if (fieldContainsValue(props?.version)) {
    base_url = base_url.replace("v1", props.version);
  }
  if (formData) {
    headers = {
      Authorization: `carrum ${authToken}`,
    };
  } else {
    headers = {
      Authorization: `carrum ${authToken}`,
      "Content-Type": "application/json",
    };
  }
  if (fieldContainsValue(props?.lat) && fieldContainsValue(props?.long)) {
    headers["x-lat"] = props.lat;
    headers["x-long"] = props.long;
  }
  try {
    if (props?.timeout) {
      abortController = new AbortController();
      abortId = setTimeout(() => abortController.abort(), props.timeout);
    }
    const response = await fetch(`${base_url}${url}`, {
      method: method,
      headers: headers,
      body: body,
      signal: abortController?.signal,
    });

    if (response.status === 403 && !_retried) {
      const result = await response.json();
      if (
        result?.error?.toLowerCase()?.includes("token") ||
        result?.message?.toLowerCase()?.includes("token")
      ) {
        const refreshed = await tryRefreshAccessToken();
        if (refreshed) {
          return connect({
            method,
            url,
            json,
            formData,
            body,
            props,
            _retried: true,
          });
        }
        logoutAndRedirect();
      }
      throw {
        data: {
          ...result,
          statusCode: response.status,
          ok: false,
          headers: response.headers,
          bodyUsed: response.bodyUsed,
        },
      };
    }

    if (json) {
      const result = await response.json();
      const results = {
        ...result,
        statusCode: response.status,
        ok: response.ok,
        headers: response.headers,
        bodyUsed: response.bodyUsed,
      };
      if (response.ok) {
        return results;
      } else {
        throw { data: results };
      }
    } else {
      if (!response.ok) {
        const result = await response.json();
        const results = {
          ...result,
          statusCode: response.status,
          ok: response.ok,
          headers: response.headers,
          bodyUsed: response.bodyUsed,
        };
        throw { data: results };
      } else {
        return response;
      }
    }
  } catch (error) {
    throw error;
  } finally {
    if (abortId) {
      clearTimeout(abortId);
    }
  }
};
const sentryConnect = withSentry(connect);
export const api2 = {
  get: async (url, props = {}) =>
    connect({ method: "GET", url: url, json: true, formData: false, props: props }),
  getData: async (url, props = {}) =>
    connect({ method: "GET", url: url, json: false, formData: false, props: props }),
  postDownload: async (url, body, props = {}) =>
    connect({
      method: "POST",
      url: url,
      json: false,
      formData: false,
      body: JSON.stringify(body),
      props: props,
    }),
  postData: async (url, formData, props = {}) =>
    connect({ method: "POST", url: url, json: true, formData: true, body: formData, props: props }),
  putData: async (url, formData, props = {}) =>
    connect({ method: "PUT", url: url, json: true, formData: true, body: formData, props: props }),
  post: async (url, body, props = {}) =>
    connect({
      method: "POST",
      url: url,
      json: true,
      formData: false,
      body: JSON.stringify(body),
      props: props,
    }),
  put: async (url, body, props = {}) =>
    connect({
      method: "PUT",
      url: url,
      json: true,
      formData: false,
      body: JSON.stringify(body),
      props: props,
    }),
  delete: async (url, body, prop = {}) =>
    connect({
      method: "DELETE",
      url: url,
      json: true,
      formData: false,
      body: JSON.stringify(body),
    }),
};
